import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Person,
  Phone,
  LocationOn,
  LocalHospital,
  AccessTime,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ipdDoneService } from '../../services/ipdDone';
import { LEAD_STATUS_LABELS } from '../../services/leads';
import LoadingSpinner from '../common/LoadingSpinner';

const PendingIPDApprovals = ({ onApprove, onReject, loading, onError }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, leadId: null });
  const [newStatus, setNewStatus] = useState('followup');

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    try {
      setLocalLoading(true);
      const data = await ipdDoneService.getPendingIPDApprovals();
      setPendingRequests(data.pending_approvals || []);
    } catch (error) {
      console.error('Failed to load pending requests:', error);
      onError('Failed to load pending IPD requests');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleApprove = async (leadId) => {
    try {
      await onApprove(leadId);
      loadPendingRequests(); // Refresh list
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleReject = async (leadId, status) => {
    try {
      await onReject(leadId, status);
      loadPendingRequests(); // Refresh list
      setRejectDialog({ open: false, leadId: null });
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const openRejectDialog = (leadId) => {
    setRejectDialog({ open: true, leadId });
    setNewStatus('followup');
  };

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, leadId: null });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase() || '??';
  };

  if (localLoading) {
    return <LoadingSpinner message="Loading pending IPD requests..." />;
  }

  if (pendingRequests.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Pending IPD Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All IPD Done requests have been processed. New requests will appear here for approval.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Pending IPD Done Requests ({pendingRequests.length})
        </Typography>
        <Button
          variant="outlined"
          onClick={loadPendingRequests}
          disabled={localLoading}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {pendingRequests.map((request) => (
          <Grid item xs={12} md={6} lg={4} key={request.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                {/* Patient Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {getInitials(request.name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" noWrap>
                      {request.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {request.phone}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Request Details */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Requested by:</strong> {request.ipd_done_requested_by_name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTime fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Requested:</strong> {formatDate(request.ipd_done_requested_at)}
                    </Typography>
                  </Box>

                  {request.city_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>City:</strong> {request.city_name}
                      </Typography>
                    </Box>
                  )}

                  {request.disease_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalHospital fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Disease:</strong> {request.disease_name}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Status Chips */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label="Pending Approval"
                    color="warning"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Lead #${request.id}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {/* Created Date */}
                <Typography variant="caption" color="text.secondary">
                  Lead created: {formatDate(request.created_at)}
                </Typography>
              </CardContent>

              {/* Action Buttons */}
              <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApprove(request.id)}
                    disabled={loading}
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => openRejectDialog(request.id)}
                    disabled={loading}
                    fullWidth
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={closeRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject IPD Done Request</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reject the IPD Done request and change the lead status.
          </Alert>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              <MenuItem value="followup">Follow-up</MenuItem>
              <MenuItem value="warm">Warm</MenuItem>
              <MenuItem value="hot">Hot</MenuItem>
              <MenuItem value="new">New</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleReject(rejectDialog.leadId, newStatus)}
            disabled={loading}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingIPDApprovals;